/*
Fork addition (Stage H): live URL anchor for the current blueprint.

The page URL doubles as the share link, Reddit-style: `#/b/<payload>`
carries the plan itself, encoded with the very same BlueprintEncoder
the Save dialog and the (upstream) Generate Link path use — compressed
form (`DAXFB<game>C$` + base64url), so a payload produced here is
byte-identical to what those paths produce for the same plan, and any
older payload still decodes through the normal load path. The encoder
options are pinned to `{blueprintCompress: true}` (Generate Link's
options) rather than the user's advanced save settings, so the anchor
is canonical and as short as possible even if the user turned
compression off for text saves.

Size guard: if the whole URL would exceed MAX_ANCHOR_URL_LENGTH the
plan is NOT put in the URL. It is stored in localStorage under a short
random key and the anchor becomes `#/b/local-<key>` instead. Such a
link only resolves on the device that created it — the loader shows a
notice rather than a blank board when the key is absent. At most
MAX_STORED_ANCHOR_PLANS plans are kept; the oldest are evicted.

This module is pure logic (storage is injected) so it can be tested
headlessly; the Vue wiring lives in composables/blueprint-anchor.ts.
*/
import {BlueprintEncoder} from './model/serializer';
import type {SavedBlueprint} from './model/saved-blueprint';
import type {GameData} from './data';

export const BLUEPRINT_HASH_PREFIX = '#/b/';
export const LOCAL_KEY_PREFIX = 'local-';
export const MAX_ANCHOR_URL_LENGTH = 2000;
export const MAX_STORED_ANCHOR_PLANS = 20;
export const ANCHOR_STORAGE_KEY = 'daxfb-anchor-plans-v1';

export const LOCAL_PLAN_MISSING_TITLE =
    'This link\'s blueprint was stored on another device';
export const LOCAL_PLAN_MISSING_MESSAGE =
    'The plan behind this link was too large to fit in the URL itself, so it was ' +
    'saved in the browser storage of the device that created the link. Open the ' +
    'link on that device, or paste the blueprint text here instead (Load button ' +
    'in the toolbar).';

export function encodePlanForAnchor(gameData: GameData, savedBlueprint: SavedBlueprint) {
    const encoder = new BlueprintEncoder(gameData, {blueprintCompress: true});
    return encoder.encode(savedBlueprint);
}

export type BlueprintAnchor =
    | {kind: 'plan'; payload: string}
    | {kind: 'local'; key: string};

//Parses a `#/b/...` hash. Returns undefined for anything else (other
//anchors, empty hash) so callers can leave foreign hashes alone.
export function parseBlueprintHash(hash: string): BlueprintAnchor | undefined {
    if(!hash.startsWith(BLUEPRINT_HASH_PREFIX))
        return undefined;
    const rest = hash.substring(BLUEPRINT_HASH_PREFIX.length).trim();
    if(!rest)
        return undefined;
    if(rest.startsWith(LOCAL_KEY_PREFIX)) {
        const key = rest.substring(LOCAL_KEY_PREFIX.length).trim();
        return key ? {kind: 'local', key} : undefined;
    }
    let payload = rest;
    //a link pasted through chat apps may arrive percent-encoded; the
    //payload alphabet itself never contains '%', so decoding is safe
    if(payload.includes('%')) {
        try {
            payload = decodeURIComponent(payload);
        } catch(err) {
            //keep the raw payload — the decoder will judge it
        }
    }
    return {kind: 'plan', payload};
}

export function buildAnchorHash(anchor: BlueprintAnchor) {
    return (anchor.kind == 'plan')
        ? BLUEPRINT_HASH_PREFIX + anchor.payload
        : BLUEPRINT_HASH_PREFIX + LOCAL_KEY_PREFIX + anchor.key;
}

export type AnchorStorage = Pick<Storage, 'getItem' | 'setItem'>;

type StoredAnchorPlan = {
    k: string;
    d: string;
    t: number;
};

function readStoredPlans(storage: AnchorStorage): StoredAnchorPlan[] {
    try {
        const raw = storage.getItem(ANCHOR_STORAGE_KEY);
        if(!raw)
            return [];
        const parsed = JSON.parse(raw);
        if(!Array.isArray(parsed))
            return [];
        return parsed.filter((entry): entry is StoredAnchorPlan => {
            return !!entry && (typeof entry.k == 'string') && (typeof entry.d == 'string');
        }).map((entry) => ({k: entry.k, d: entry.d, t: (typeof entry.t == 'number') ? entry.t : 0}));
    } catch(err) {
        return [];
    }
}

function writeStoredPlans(storage: AnchorStorage, plans: StoredAnchorPlan[]): boolean {
    try {
        storage.setItem(ANCHOR_STORAGE_KEY, JSON.stringify(plans));
        return true;
    } catch(err) {
        //storage full / blocked — the caller treats this as "cannot store"
        return false;
    }
}

export function resolveLocalPlan(storage: AnchorStorage, key: string): string | undefined {
    return readStoredPlans(storage).find((entry) => entry.k == key)?.d;
}

const KEY_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

function mintKey(existing: Set<string>): string {
    for(let attempt = 0; attempt < 16; ++attempt) {
        let key = '';
        const random = new Uint8Array(8);
        if(globalThis.crypto?.getRandomValues) {
            globalThis.crypto.getRandomValues(random);
        } else {
            for(let i = 0; i < random.length; ++i)
                random[i] = Math.floor(Math.random() * 256);
        }
        for(const byte of random)
            key += KEY_ALPHABET.charAt(byte % KEY_ALPHABET.length);
        if(!existing.has(key))
            return key;
    }
    //astronomically unlikely collision streak — lengthen rather than fail
    return mintKey(existing) + 'x';
}

//Stores the plan string and returns the key it lives under, or '' when
//storage refused the write. When preferredKey names an existing entry
//that entry is updated in place (the anchor stays stable while a big
//plan keeps being edited); otherwise a fresh key is minted. Newest
//entries sort first; anything past MAX_STORED_ANCHOR_PLANS is evicted.
export function storeLocalPlan(storage: AnchorStorage, planData: string, preferredKey?: string): string {
    const plans = readStoredPlans(storage);
    const key = (preferredKey && plans.some((entry) => entry.k == preferredKey))
        ? preferredKey
        : mintKey(new Set(plans.map((entry) => entry.k)));
    const entry: StoredAnchorPlan = {k: key, d: planData, t: Date.now()};
    const rest = plans.filter((e) => e.k != key);
    if(!writeStoredPlans(storage, [entry, ...rest].slice(0, MAX_STORED_ANCHOR_PLANS)))
        return '';
    return key;
}
